/**
 * How loud the interface is.
 *
 * Kept apart from the world on purpose. Menu sound is the first thing a player turns off and the
 * last thing they want to lose gunfire over, and a shared bus would make that one decision.
 *
 * Writes to [the mix levels](../../../audio/mixLevels.ts), which the bus tree reads and subscribes
 * to, so a drag is heard while the thumb is still down and a level set before any audio context
 * exists is applied to the tree the moment one does.
 *
 * The bounds, the default and the curve between a slider and a gain are that module's, not this
 * file's. A knob names a quantity and hands a value to whoever owns it, and four copies of a
 * loudness curve is four chances for one of them to be wrong.
 */

import { MAX_MIX_LEVEL, MIN_MIX_LEVEL, DEFAULT_MIX_LEVELS, setMixLevel } from '@/audio/mixLevels';
import { rangeApply } from '@/ui/settings/controls/rangeSlider';
import { SETTING_UI_LEVEL_LABEL, SETTING_UI_LEVEL_HINT } from '@/text/settingsText';
import type { SettingDefinition } from '@/ui/settings/settingContract';

/** Five percent a step: fine enough to find a level, coarse enough to land on one again. */
const STEP = 0.05;

export const UI_LEVEL: SettingDefinition = {
  id: 'uiLevel',
  section: 'audio',
  label: SETTING_UI_LEVEL_LABEL,
  hint: SETTING_UI_LEVEL_HINT,
  control: {
    kind: 'level',
    min: MIN_MIX_LEVEL,
    max: MAX_MIX_LEVEL,
    step: STEP,
    // A percentage, because a volume is a share of full and 0.65 is a number nobody can picture.
    format: (value: number): string => `${String(Math.round(value * 100))}%`,
    // Imported at the moment it is pressed, never at the top of the file. The settings registry is
    // reachable from the menu, so a static import here would put the audio engine and everything it
    // reaches into the chunk that draws the cards, for a player who has not opened settings.
    audition: (): void => {
      void import('@/audio/testTone').then((module) => {
        module.playTestTone('ui');
      });
    },
  },
  defaultValue: DEFAULT_MIX_LEVELS.ui,
  apply: rangeApply((value: number): void => {
    setMixLevel('ui', value);
  }),
};
